import colors from 'theme/colors';

/**
 * Tokens compartilhados da visão de vendas (ingressos e produtos).
 *
 * A cor codifica **desempenho de venda** e nada mais. Uma escala só, aplicada
 * na seção "Desempenho por ingresso/produto": cinza = parado, âmbar = devagar,
 * roxo = vendendo, verde = forte.
 *
 * Regra que originou esta escala: a tela antiga pintava "99% disponível" de
 * verde — cor de sucesso comunicando fracasso de vendas. Aqui o tom sempre
 * acompanha o quanto foi vendido, nunca o quanto sobrou.
 *
 * Acessibilidade: `text` é sempre a variante escurecida, para uso sobre fundo
 * claro (≥4,5:1). Os tons de `fill` só preenchem barra, onde não há leitura.
 */

export const TONE = {
    NONE: 'none',
    SLOW: 'slow',
    SELLING: 'selling',
    STRONG: 'strong',
    SOLD_OUT: 'soldOut',
};

export const tone = {
    [TONE.NONE]: {
        text: '#5A5A5A',        // 7,0:1 sobre branco
        fill: '#C7C7C7',
        track: '#ECECEC',
        soft: '#F5F5F7',
        accent: '#C2C2C8',
        border: '#E2E2E6',
    },
    [TONE.SLOW]: {
        text: '#8A5300',        // 5,3:1 sobre branco
        fill: '#F0A22E',
        track: '#FBEBD3',
        soft: '#FFF8EC',
        accent: '#E8951A',
        border: '#F3DFAE',
    },
    [TONE.SELLING]: {
        text: '#6D14CC',        // 7,4:1 sobre branco
        fill: colors.secondaryPurple,
        track: '#EBDCFB',
        soft: '#F7EFFF',
        accent: colors.secondaryPurple,
        border: '#DFC8F8',
    },
    [TONE.STRONG]: {
        text: '#157F3C',        // 5,2:1 sobre branco
        fill: '#2BC866',
        track: '#D8F3E3',
        soft: '#F1FBF5',
        accent: '#1FA855',
        border: '#B7E5C9',
    },
    [TONE.SOLD_OUT]: {
        text: '#0F6B31',        // 6,3:1 sobre branco
        fill: '#157F3C',
        track: '#D8F3E3',
        soft: '#EDF9F2',
        accent: '#157F3C',
        border: '#A9DFC0',
    },
};

/** Traduz um percentual vendido (0–100) no tom semântico correspondente. */
export function toneForPercent(percent) {
    if (percent >= 100) return TONE.SOLD_OUT;
    if (percent >= 60) return TONE.STRONG;
    if (percent >= 20) return TONE.SELLING;
    if (percent > 0) return TONE.SLOW;
    return TONE.NONE;
}

/**
 * "Esgotando" é oportunidade, não problema — sinaliza a hora de abrir novo lote
 * ou ajustar preço. Fica separado da escala de desempenho justamente para não
 * disputar o âmbar com "vendendo devagar", que significa o oposto.
 */
export const SELLING_OUT_THRESHOLD = 90;

export function isSellingOut(percent) {
    return percent >= SELLING_OUT_THRESHOLD && percent < 100;
}

export const STATUS = {
    AVAILABLE: 'available',
    SOLD_OUT: 'soldOut',
    DISABLED: 'disabled',
};

export const statusLabel = {
    [STATUS.AVAILABLE]: 'Disponível',
    [STATUS.SOLD_OUT]: 'Esgotado',
    [STATUS.DISABLED]: 'Desativado',
};

export const statusChip = {
    [STATUS.AVAILABLE]: { bg: '#EDF9F2', text: '#0F6B31', border: '#B7E5C9' },
    [STATUS.SOLD_OUT]: { bg: '#F7EFFF', text: '#6D14CC', border: '#DFC8F8' },
    [STATUS.DISABLED]: { bg: '#F4F4F4', text: '#5A5A5A', border: '#DDDDDD' },
};

/** Deriva o status de um item a partir dos seus lotes. */
export function statusForBatches(batches = []) {
    const hasAvailable = batches.some((b) => b?.quantity_available > 0 && Number(b?.is_active) === 1);
    if (hasAvailable) return STATUS.AVAILABLE;

    const allSoldOut = batches.length > 0 && batches.every((b) => b?.quantity_available === 0);
    if (allSoldOut) return STATUS.SOLD_OUT;

    return STATUS.DISABLED;
}

/** Status de um lote isolado. */
export function statusForBatch(batch) {
    if (batch?.quantity_available === 0) return STATUS.SOLD_OUT;
    if (Number(batch?.is_active) === 0) return STATUS.DISABLED;
    return STATUS.AVAILABLE;
}

/** Agrega quantidades e valores dos lotes de um item. */
export function summarizeTicket(ticket) {
    const batches = ticket?.batches || [];

    return batches.reduce(
        (acc, batch) => {
            const quantity = Number(batch?.quantity) || 0;
            const available = Number(batch?.quantity_available) || 0;

            acc.sold += quantity - available;
            acc.total += quantity;
            acc.available += available;
            acc.collected += Number(batch?.value_sold_without_tax) || 0;
            acc.estimated += quantity * (Number(batch?.price) || 0);

            return acc;
        },
        { sold: 0, total: 0, available: 0, collected: 0, estimated: 0 }
    );
}

export const percentOf = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

/** Arredonda para inteiro sem transformar uma venda real em "0%". */
export const displayPercent = (value) => {
    if (value <= 0) return 0;
    if (value < 1) return 1;
    return Math.round(value);
};

export const formatInt = (value) => Number(value || 0).toLocaleString('pt-BR');
